import { Icon } from '@iconify/react';
import { Button, Spinner, Tooltip } from '@nextui-org/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import DataView from '../../components/data-view/data-view';
import ListCard from '../../components/list-card';
import { TableConfig } from '../../components/table/types';
import { useCreateOrUpdatePractice } from '../../hooks/queries/practices';
import PromptService from '../../services/prompt-service';
import { useAuthStore } from '../../store/use-auth-store';
import { PromptData, StandardResponse } from '../../types/agent';

const NewPractice = ({ title, tag }) => {
  const navigate = useNavigate();
  const createPracticeMutation = useCreateOrUpdatePractice();
  const orgId = useAuthStore((state) => state.orgId);
  const [loadingPracticeId, setLoadingPracticeId] = React.useState<
    number | null
  >(null);
  const [viewType, setViewType] = React.useState<'tile' | 'list'>('tile');

  const { data, isLoading, isError } = useQuery<StandardResponse<PromptData>>({
    queryKey: [`agent-${title}`, { is_active: true, tag }],
    queryFn: async (): Promise<StandardResponse<PromptData>> => {
      const response = await PromptService.getPrompts({
        filters: {
          page: 1,
          is_active: true,
          tag,
        },
      });
      return response as StandardResponse<PromptData>;
    },
  });

  const handlePracticeClick = async (practice: PromptData) => {
    try {
      setLoadingPracticeId(practice.id);
      const response = await createPracticeMutation.mutateAsync({
        name: practice.name,
        org_id: orgId,
      });

      navigate(`${response.id}/run/?action=${encodeURIComponent(practice.name)}&id=${response.id}`);
    } catch (error) {
      toast.error('Failed to create practice session');
      console.log(error);
    } finally {
      setLoadingPracticeId(null);
    }
  };

  if (isLoading)
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  if (isError)
    return (
      <div className="text-center text-danger mt-8">
        Error loading agents. Please try again later.
      </div>
    );

  const practiceTableConfig: TableConfig = {
    columns: [
      {
        uid: 'name',
        name: 'Name',
        sortable: true,
      },
      //   {
      //     uid: "system_prompt",
      //     name: "Description",
      //     sortable: true,
      //     width: "50%",
      //     renderCell: (practice: (typeof prompts)[0]) => (
      //       <div className="truncate max-w-md">{practice.description}</div>
      //     ),
      //   },
      {
        uid: 'actions',
        name: 'Actions',
        renderCell: (practice: PromptData) => (
          <div className="flex gap-2">
            <Tooltip content="Start Practice">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onClick={() => handlePracticeClick(practice)}
                disabled={loadingPracticeId === practice.id}
                aria-label="Start"
              >
                {loadingPracticeId === practice.id ? (
                  <Icon
                    icon="lucide:loader-circle"
                    width="20"
                    className="animate-spin"
                  />
                ) : (
                  <Icon icon="proicons:play" width="20" />
                )}
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ],
    initialVisibleColumns: ['name', 'description', 'actions'],
    searchable: true,
    searchPlaceholder: 'Search practices...',
    searchKey: 'name',

    pagination: {
      enabled: true,
      initialRowsPerPage: 100,
      rowsPerPageOptions: [],
      showControls: true,
      showShadow: true,
    },

    title: 'Leadership Practices',
    description: 'Start a new practice session',
    stickyHeader: true,
    maxHeight: '700px',
  };

  const renderCard = (practice: PromptData) => (
    <ListCard
      key={practice.id}
      title={practice.name}
      //   description={practice.system_prompt}
      onStart={() => handlePracticeClick(practice)}
      isStarting={loadingPracticeId === practice.id}
    />
  );

  return (
    <div className="space-y-4">
      <DataView<PromptData>
        title={title}
        data={data?.results}
        tableConfig={practiceTableConfig}
        viewType={viewType}
        onViewChange={setViewType}
        renderCard={renderCard}
      />
    </div>
  );
};

export default NewPractice;
